import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

// Components
import { PageHeader } from "../components/PageSpecificComponents";
import {
  Card,
  Header,
  DatePicker,
  Body,
  Button,
  EmptyState,
  Search,
  ToggleSwitch
} from "../components/UI";
import { Label, Paragraph } from "../components/typography";
import Row from "../components/lists/Row";
import ServiceItem from "../components/lists/services/Service.item";
import AddExternalServiceModal from "../features/addExternalService/components/AddExternalServiceModal/AddExternalServiceModal";

// Builders
import {
  statusFilters,
  tableHeadBuilder,
  datePresets,
  dateModeOptions,
  dateModeHints,
  DATE_PRESET_TODAY,
  DATE_PRESET_CUSTOM,
  DATE_MODE_ACTIVE,
  sortOptions,
  SORT_CREATED_NEWEST
} from "../builders/services.builder";
import { STATUS_THEME } from "../constants/statusTheme";

// Constants
import * as filtersConstats from "../constants/services.constants";

// Actions
import {
  getServices,
  getMoreServices,
  openTicketDetails
} from "../redux/actions/services.actions";

// Services
import { disableScroll, isEmpty } from "../utils";

// Hooks
import useDebounce from "../hooks/useDebounce";

// Styles
import { ServicesStyles } from "../pagesStyles/services.styles";
import * as buttonDesignType from "../builders/buttonDesign.types";

const DEFAULT_STATUS_FILTERS = [
  filtersConstats.FILTERS_ACCEPTED,
  filtersConstats.FILTERS_IN_PROGRESS,
  filtersConstats.FILTERS_PAUSED
];

const Services = () => {
  const [isExternalServiceModalOpen, setIsExternalServiceModalOpen] =
    useState(false);
  const dispatch = useDispatch();
  const {
    services,
    lastService,
    hasMore,
    statusCounts,
    isFetchingServices,
    shouldRefetchServicesAfterAction
  } = useSelector(state => state.servicesReducer);
  const { activeBranch } = useSelector(state => state.app);

  // Query states
  const [chosenFilters, setChosenFilters] = useState(DEFAULT_STATUS_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const { setSearchQuery, debouncedSearchQuery } = useDebounce(400);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [chosenDatePreset, setChosenDatePreset] = useState(DATE_PRESET_TODAY);
  const [dateMode, setDateMode] = useState(DATE_MODE_ACTIVE);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  // Draft state — edited inside dialog, committed only on Apply click.
  const [draftDateFrom, setDraftDateFrom] = useState(new Date());
  const [draftDateTo, setDraftDateTo] = useState(new Date());
  const [draftDatePreset, setDraftDatePreset] = useState(DATE_PRESET_TODAY);
  const [draftDateMode, setDraftDateMode] = useState(DATE_MODE_ACTIVE);
  const [sortBy, setSortBy] = useState(SORT_CREATED_NEWEST);
  const [openMenu, setOpenMenu] = useState(null); // 'preset' | 'mode' | 'sort' | null

  // Loading shimmer for search/filter fetches — held for at least 1s so a fast
  // response doesn't just flicker, otherwise as long as the request runs.
  const [showShimmer, setShowShimmer] = useState(false);
  const shimmerStartRef = useRef(0);
  useEffect(() => {
    if (isFetchingServices) {
      shimmerStartRef.current = Date.now();
      setShowShimmer(true);
      return;
    }
    if (!showShimmer) return;
    const remaining = Math.max(0, 1000 - (Date.now() - shimmerStartRef.current));
    const timer = setTimeout(() => setShowShimmer(false), remaining);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingServices]);

  const dateFilterRef = useRef(null);
  const presetMenuRef = useRef(null);
  const modeMenuRef = useRef(null);
  const sortMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) {
        setDateDialogOpen(false);
        if (openMenu === "preset" || openMenu === "mode") {
          setOpenMenu(null);
        }
      }
      if (
        openMenu === "preset" &&
        presetMenuRef.current &&
        !presetMenuRef.current.contains(e.target)
      ) {
        setOpenMenu(null);
      } else if (
        openMenu === "mode" &&
        modeMenuRef.current &&
        !modeMenuRef.current.contains(e.target)
      ) {
        setOpenMenu(null);
      } else if (
        openMenu === "sort" &&
        sortMenuRef.current &&
        !sortMenuRef.current.contains(e.target)
      ) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu]);

  const openDateDialog = () => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setDraftDatePreset(chosenDatePreset);
    setDraftDateMode(dateMode);
    setDateDialogOpen(true);
  };

  const applyDateDialog = () => {
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setChosenDatePreset(draftDatePreset);
    setDateMode(draftDateMode);
    setDateDialogOpen(false);
  };

  useEffect(() => {
    setSearchQuery(searchInput);
  }, [searchInput, setSearchQuery]);

  const filtersKey = useMemo(
    () => [...chosenFilters].sort().join(","),
    [chosenFilters]
  );

  const toggleStatus = status => {
    const next = chosenFilters.includes(status)
      ? chosenFilters.filter(s => s !== status)
      : [...chosenFilters, status];
    setChosenFilters(next);
  };

  const convertDateToString = date => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = `${date.getFullYear()}`;

    const formatedDay = day < 10 ? `0${day}` : `${day}`;
    const formatedMonth = month < 10 ? `0${month}` : `${month}`;

    return `${year}-${formatedMonth}-${formatedDay}`;
  };

  const applyDraftPreset = preset => {
    if (preset.range) {
      const { from, to } = preset.range();
      setDraftDateFrom(from);
      setDraftDateTo(to);
    }
    setDraftDatePreset(preset.value);
  };

  const handleDraftDateChange = (which, value) => {
    if (which === "from") {
      setDraftDateFrom(value);
      if (value > draftDateTo) setDraftDateTo(value);
    } else {
      setDraftDateTo(value);
      if (value < draftDateFrom) setDraftDateFrom(value);
    }
    setDraftDatePreset(DATE_PRESET_CUSTOM);
  };

  const dateFilterPayload = useMemo(
    () => ({
      enabled: dateFilterEnabled,
      from: dateFilterEnabled ? convertDateToString(dateFrom) : null,
      to: dateFilterEnabled ? convertDateToString(dateTo) : null,
      mode: dateFilterEnabled ? dateMode : null
    }),
    [dateFilterEnabled, dateFrom, dateTo, dateMode]
  );

  const dateFilterKey = `${dateFilterPayload.enabled}|${dateFilterPayload.from}|${dateFilterPayload.to}|${dateFilterPayload.mode}`;

  const formatDisplayDate = date => {
    if (!date) return "";
    const d = date.getDate();
    const m = date.getMonth() + 1;
    const y = date.getFullYear();
    return `${m < 10 ? "0" + m : m}/${d < 10 ? "0" + d : d}/${y}`;
  };

  const dateModeTitle =
    dateModeOptions.find(o => o.value === dateMode)?.title ?? "";

  const datePresetTitle =
    datePresets.find(p => p.value === chosenDatePreset)?.title ?? "";

  const dateRangeLabel =
    chosenDatePreset === DATE_PRESET_CUSTOM
      ? `${formatDisplayDate(dateFrom)} → ${formatDisplayDate(dateTo)}`
      : datePresetTitle;

  const dateTriggerLabel = `${dateModeTitle} · ${dateRangeLabel}`;

  useEffect(() => {
    dispatch(
      getServices({
        dateFilter: dateFilterPayload,
        groupQuery: chosenFilters,
        searchQuery: debouncedSearchQuery,
        limit: 20,
        sortBy
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch, filtersKey, debouncedSearchQuery, dateFilterKey, sortBy, dispatch]);

  useEffect(() => {
    if (shouldRefetchServicesAfterAction === "refetch") {
      dispatch(
        getServices({
          dateFilter: dateFilterPayload,
          groupQuery: chosenFilters,
          searchQuery: debouncedSearchQuery,
          limit: 20,
          sortBy
        })
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateFilterKey,
    filtersKey,
    debouncedSearchQuery,
    shouldRefetchServicesAfterAction,
    services,
    sortBy,
    dispatch
  ]);

  const fetchMoreServices = () => {
    dispatch(
      getMoreServices({
        dateFilter: dateFilterPayload,
        groupQuery: chosenFilters,
        searchQuery: debouncedSearchQuery,
        limit: 20,
        lastService: lastService,
        sortBy
      })
    );
  };

  return (
    <ServicesStyles>
      <PageHeader title="Services">
        <Button
          buttonDesignType={buttonDesignType.PRIMARY}
          onClick={() => setIsExternalServiceModalOpen(true)}
        >
          + Add External Service
        </Button>
      </PageHeader>
      <Card className="card" rounded>
        <Header className="filters">
          <div className="searchWrapper">
            <div className="searchInner">
              <Search
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search service"
              />
            </div>
            <p className="searchHint">
              *Search by ID, customer, VIN, truck/trailer, service type
            </p>
          </div>
          <div className="dateFilterWrapper" ref={dateFilterRef}>
            <div className="dateFilterRow">
              <ToggleSwitch
                value={dateFilterEnabled}
                name="dateFilterEnabled"
                handleChange={(_, v) => {
                  setDateFilterEnabled(v);
                  if (!v) setDateDialogOpen(false);
                }}
              />
              <span className="dateFilterToggleLabel">Filter by date</span>
              {dateFilterEnabled && (
                <div className="dateTriggerAnchor">
                  <button
                    type="button"
                    className={`dateTrigger ${dateDialogOpen ? "open" : ""}`}
                    onClick={() =>
                      dateDialogOpen
                        ? setDateDialogOpen(false)
                        : openDateDialog()
                    }
                  >
                    <span className="dateTriggerText">{dateTriggerLabel}</span>
                    <span className="chevron" />
                  </button>
                  {dateDialogOpen && (
                    <div className="dateDialog">
                <div className="datePresetRow">
                  <Label>Period</Label>
                  <div className="customDropdown" ref={presetMenuRef}>
                    <button
                      type="button"
                      className={`customDropdownTrigger ${
                        openMenu === "preset" ? "open" : ""
                      }`}
                      onClick={() =>
                        setOpenMenu(prev => (prev === "preset" ? null : "preset"))
                      }
                    >
                      <span>
                        {datePresets.find(p => p.value === draftDatePreset)
                          ?.title}
                      </span>
                      <span className="chevron" />
                    </button>
                    {openMenu === "preset" && (
                      <div className="customDropdownMenu">
                        {datePresets.map(p => (
                          <div
                            key={p.value}
                            className={`customDropdownItem ${
                              draftDatePreset === p.value ? "selected" : ""
                            }`}
                            onClick={() => {
                              applyDraftPreset(p);
                              setOpenMenu(null);
                            }}
                          >
                            {p.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {draftDatePreset === DATE_PRESET_CUSTOM && (
                  <div className="datePickersRow">
                    <div className="datePickerCol">
                      <Label>From</Label>
                      <DatePicker
                        style={{ marginTop: 2, width: 180 }}
                        value={draftDateFrom}
                        onChange={(_, v) => handleDraftDateChange("from", v)}
                        shouldHaveError={false}
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="datePickerCol">
                      <Label>To</Label>
                      <DatePicker
                        style={{ marginTop: 2, width: 180 }}
                        value={draftDateTo}
                        onChange={(_, v) => handleDraftDateChange("to", v)}
                        shouldHaveError={false}
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                  </div>
                )}
                <div className="dateModeRow">
                  <Label>Mode</Label>
                  <div className="customDropdown" ref={modeMenuRef}>
                    <button
                      type="button"
                      className={`customDropdownTrigger ${
                        openMenu === "mode" ? "open" : ""
                      }`}
                      onClick={() =>
                        setOpenMenu(prev => (prev === "mode" ? null : "mode"))
                      }
                    >
                      <span>
                        {dateModeOptions.find(o => o.value === draftDateMode)
                          ?.title}
                      </span>
                      <span className="chevron" />
                    </button>
                    {openMenu === "mode" && (
                      <div className="customDropdownMenu">
                        {dateModeOptions.map(opt => (
                          <div
                            key={opt.value}
                            className={`customDropdownItem ${
                              draftDateMode === opt.value ? "selected" : ""
                            }`}
                            onClick={() => {
                              setDraftDateMode(opt.value);
                              setOpenMenu(null);
                            }}
                          >
                            {opt.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="dateHint">{dateModeHints[draftDateMode]}</p>
                </div>
                <div className="dateDialogActions">
                  <Button
                    className="applyBtn"
                    buttonDesignType={buttonDesignType.PRIMARY}
                    onClick={applyDateDialog}
                  >
                    Apply
                  </Button>
                </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Header>
        <Header className="statusFilters">
          <div className="chips">
            {statusFilters.map(f => {
              const on = chosenFilters.includes(f.value);
              const palette = STATUS_THEME[f.key];
              const count = statusCounts?.[f.value];
              const style = on
                ? {
                    ["--chip-bg"]: palette.bg,
                    ["--chip-fg"]: palette.fg,
                    ["--chip-color"]: palette.chip,
                    ["--chip-border"]: palette.chip + "55"
                  }
                : undefined;
              return (
                <button
                  key={f.value}
                  type="button"
                  className={`chip ${on ? "on" : ""}`}
                  style={style}
                  onClick={() => toggleStatus(f.value)}
                >
                  <span className="dot" />
                  {f.label}
                  <span className="count">{count || 0}</span>
                </button>
              );
            })}
          </div>
          <div className="sortFilterWrapper" ref={sortMenuRef}>
            <span className="sortFilterLabel">Sort by</span>
            <button
              type="button"
              className={`sortTrigger ${openMenu === "sort" ? "open" : ""}`}
              onClick={() =>
                setOpenMenu(prev => (prev === "sort" ? null : "sort"))
              }
            >
              <span className="sortTriggerText">
                {sortOptions.find(o => o.value === sortBy)?.title}
              </span>
              <span className="chevron" />
            </button>
            {openMenu === "sort" && (
              <div className="sortMenu">
                {sortOptions.map(opt => (
                  <div
                    key={opt.value}
                    className={`sortMenuItem ${
                      sortBy === opt.value ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSortBy(opt.value);
                      setOpenMenu(null);
                    }}
                  >
                    {opt.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Header>
      </Card>
      <Card className="tableCard" rounded>
        <div className="tableScroll">
          <Body className="tableHead">
            {tableHeadBuilder.map(({ title, className }, i) => (
              <Paragraph key={i} className={`headTitle ${className}`}>
                {title}
              </Paragraph>
            ))}
          </Body>
          {showShimmer ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div className="skRow" key={i}>
                <div className="ID"><span className="sk-line" style={{ width: "50%" }} /></div>
                <div className="status"><span className="sk-pill" /></div>
                <div className="start"><span className="sk-line" style={{ width: "70%" }} /></div>
                <div className="finish"><span className="sk-line" style={{ width: "70%" }} /></div>
                <div className="user"><span className="sk-line" style={{ width: "80%" }} /></div>
                <div className="serviceType"><span className="sk-line" style={{ width: "75%" }} /></div>
                <div className="truck"><span className="sk-line" style={{ width: "70%" }} /></div>
                <div className="trailer"><span className="sk-line" style={{ width: "70%" }} /></div>
                <div className="workers"><span className="sk-avatar" /></div>
              </div>
            ))
          ) : isEmpty(services) && !hasMore ? (
            <EmptyState
              title="No services yet"
              subtitle="Services will appear here once they are scheduled."
            />
          ) : (
            <InfiniteScroll
              dataLength={services.length}
              next={fetchMoreServices}
              hasMore={hasMore}
            >
              {services.map(service => (
                <Row
                  onClick={() => {
                    disableScroll();
                    dispatch(openTicketDetails(service.id, service.branchId));
                  }}
                  className="listRow"
                  key={service.id}
                >
                  <ServiceItem {...service} />
                </Row>
              ))}
            </InfiniteScroll>
          )}
        </div>
      </Card>

      {/* Service detail drawer + workers modal are rendered globally
          (GlobalServiceDrawers in AppShell) — openTicketDetails above
          drives them through redux. */}
      {isExternalServiceModalOpen && (
        <AddExternalServiceModal
          closeModal={() => setIsExternalServiceModalOpen(false)}
        />
      )}
    </ServicesStyles>
  );
};

export default Services;
